import {
    ILatLong,
    IShipment,
} from '../components/Applications/Intransit/interfaces'
import { MapTypes } from '../constants/constants'
import { MapData } from '../services/apiInterfaces'

// GeoJSON types
const geoJSONTypes = [
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
    'Feature',
    'FeatureCollection',
]
// Validation function for GeoJSON

export function isValidGeoJSON(geoJSON: unknown): boolean {
    // Check if geoJSON is an object
    if (typeof geoJSON !== 'object' || geoJSON === null) {
        return false
    }

    const g = geoJSON as {
        type: string
        features?: unknown[]
        geometry?: unknown
        coordinates?: unknown[]
    }

    // Check for the required 'type' property
    if (!Object.hasOwn(g, 'type') || !geoJSONTypes.includes(g.type)) {
        return false
    }

    // For FeatureCollection, validate the features array
    if (g.type === 'FeatureCollection') {
        if (!Array.isArray(g.features)) {
            return false
        }
        // Recursively validate each feature
        return g.features.every(isValidGeoJSON)
    }

    // For Feature, validate geometry
    if (g.type === 'Feature') {
        return isValidGeoJSON(g.geometry)
    }

    // For Geometry types, validate coordinates
    if (g.type === 'Point' && Array.isArray(g.coordinates)) {
        return g.coordinates.length === 2 && isValidCoordinate(g.coordinates)
    }

    if (
        ['LineString', 'MultiPoint'].includes(g.type) &&
        Array.isArray(g.coordinates)
    ) {
        return g.coordinates.every(isValidCoordinate)
    }

    if (
        ['Polygon', 'MultiLineString'].includes(g.type) &&
        Array.isArray(g.coordinates)
    ) {
        // Each ring must have at least 4 positions and be closed (first == last)
        return (
            g.coordinates.length > 0 &&
            g.coordinates.every(
                (ring: any) =>
                    Array.isArray(ring) &&
                    ring.length >= 4 &&
                    isValidCoordinate(ring[0]) &&
                    isValidCoordinate(ring[ring.length - 1]) &&
                    ring[0][0] === ring[ring.length - 1][0] &&
                    ring[0][1] === ring[ring.length - 1][1] &&
                    ring.every(isValidCoordinate),
            )
        )
    }

    if (g.type === 'MultiLineString' && Array.isArray(g.coordinates)) {
        // Each line must have at least 2 positions
        return (
            g.coordinates.length > 0 &&
            g.coordinates.every(
                (line: any) =>
                    Array.isArray(line) &&
                    line.length >= 2 &&
                    line.every(isValidCoordinate),
            )
        )
    }

    if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
        return g.coordinates.every(
            (polygon: any) =>
                Array.isArray(polygon) &&
                polygon.every(
                    (ring: any) =>
                        Array.isArray(ring) && ring.every(isValidCoordinate),
                ),
        )
    }

    return false
}
// Helper function to validate coordinates
function isValidCoordinate(coord: any): boolean {
    return (
        Array.isArray(coord) &&
        coord.length === 2 &&
        typeof coord[0] === 'number' &&
        typeof coord[1] === 'number' &&
        coord[0] >= -180 &&
        coord[0] <= 180 &&
        coord[1] >= -90 &&
        coord[1] <= 90
    )
}

export const isValidMapData = (item: MapData): boolean => {
    return !!(item.lat && item.long && item.lat !== 0 && item.long !== 0)
}

export const isValidLatLong = (latLong: ILatLong): boolean => {
    return (
        !!latLong.lat &&
        !!latLong.long &&
        latLong.lat !== 0 &&
        latLong.long !== 0
    )
}

export const calculatePolygonCenter = (
    coordinates: GeoJSON.Position[],
): [number, number] => {
    if (!coordinates || coordinates?.length === 0) {
        return [0, 0] // Handle empty polygons
    }

    let totalX = 0
    let totalY = 0

    for (const [x, y] of coordinates) {
        totalX += x
        totalY += y
    }

    const centerX = totalX / coordinates.length
    const centerY = totalY / coordinates.length

    return [centerX, centerY]
}

export const isGeometryWithCoordinates = (
    geometry: GeoJSON.Geometry | null,
): geometry is
    | GeoJSON.Point
    | GeoJSON.LineString
    | GeoJSON.Polygon
    | GeoJSON.MultiPoint
    | GeoJSON.MultiLineString
    | GeoJSON.MultiPolygon => {
    return (
        geometry != null &&
        (geometry.type === 'Point' ||
            geometry.type === 'LineString' ||
            geometry.type === 'Polygon' ||
            geometry.type === 'MultiPoint' ||
            geometry.type === 'MultiLineString' ||
            geometry.type === 'MultiPolygon')
    )
}

export const hasValidPosition = (
    marker: IShipment,
): marker is IShipment & { position: { lat: number; lng: number } } => {
    return (
        !!marker.position &&
        typeof marker.position.lat === 'number' &&
        typeof marker.position.lng === 'number'
    )
}

export interface LatLng {
    lat: number
    lng: number
}

interface RoadRouteResult {
    distance: string
    duration: string
}

export const getRoadDistanceAndTime = (
    origin: LatLng,
    destination: LatLng,
): Promise<RoadRouteResult | null> => {
    return new Promise((resolve) => {
        if (
            typeof window.google === 'undefined' ||
            typeof window.google.maps === 'undefined' ||
            typeof window.google.maps.DirectionsService !== 'function'
        ) {
            console.error(
                'Google Maps API is not loaded or DirectionsService is unavailable.',
            )
            resolve(null)
            return
        }

        const directionsService = new google.maps.DirectionsService()

        directionsService.route(
            {
                origin,
                destination,
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (response: any, status) => {
                if (
                    status === google.maps.DirectionsStatus.OK &&
                    response.routes.length > 0
                ) {
                    const leg = response.routes[0].legs[0]
                    resolve({
                        distance:
                            (leg.distance?.value || 0) / 1000 +
                            (leg.distance?.value ? ' km' : ''),
                        duration: leg.duration?.text || 'Unknown',
                    })
                } else {
                    resolve(null)
                }
            },
        )
    })
}

export const generateDonutClusterIcon = (
    redCount: number,
    greenCount: number,
    options?: {
        size?: number
        ringWidth?: number
        centerColor?: string
        textColor?: string
        fontSize?: number
        fontWeight?: number
    },
): string => {
    const size = options?.size ?? 100
    const ringWidth = options?.ringWidth ?? 12
    const centerColor = options?.centerColor ?? '#1659d6ff'
    const textColor = options?.textColor ?? 'white'
    const fontSize = options?.fontSize ?? 40
    const fontWeight = options?.fontWeight ?? 550

    const radius = size / 2
    const innerRadius = radius - ringWidth
    const total = redCount + greenCount

    if (total <= 0) return ''

    const redRatio = redCount / total
    const greenRatio = greenCount / total

    const minSweep = 0.01
    const TWO_PI = Math.PI * 2
    const maxSweep = TWO_PI - 0.001

    const redAngle = redRatio > 0 ? Math.max(redRatio * TWO_PI, minSweep) : 0
    const greenAngle =
        greenRatio > 0 ? Math.max(greenRatio * TWO_PI, minSweep) : 0

    // Prevent total sweep from exceeding 360°
    const totalSweep = redAngle + greenAngle
    const adjustFactor = totalSweep > TWO_PI ? TWO_PI / totalSweep : 1

    const adjustedRedAngle = redAngle * adjustFactor
    const adjustedGreenAngle = greenAngle * adjustFactor

    const polarToCartesian = (angle: number, r: number) => ({
        x: radius + r * Math.cos(angle),
        y: radius + r * Math.sin(angle),
    })

    const createArc = (start: number, sweep: number, color: string): string => {
        if (sweep >= maxSweep) {
            // Special case: full ring — draw as two arcs
            const mid = start + Math.PI
            return (
                createArc(start, Math.PI, color) +
                createArc(mid, Math.PI, color)
            )
        }

        const end = start + sweep
        const largeArc = sweep > Math.PI ? 1 : 0

        const outerStart = polarToCartesian(start, radius)
        const outerEnd = polarToCartesian(end, radius)
        const innerEnd = polarToCartesian(end, innerRadius)
        const innerStart = polarToCartesian(start, innerRadius)

        return `
            <path
                d="M ${outerStart.x} ${outerStart.y}
                   A ${radius} ${radius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}
                   L ${innerEnd.x} ${innerEnd.y}
                   A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}
                   Z"
                fill="${color}" opacity="1.0"
            />
        `
    }

    let start = 0
    const greenArc =
        greenCount > 0 ? createArc(start, adjustedGreenAngle, 'green') : ''
    start += adjustedGreenAngle
    const redArc = redCount > 0 ? createArc(start, adjustedRedAngle, 'red') : ''

    const centerCircle = `
        <circle cx="${radius}" cy="${radius}" r="${innerRadius - 1}" fill="${centerColor}" />
    `

    const text = `
        <text x="${radius}" y="${radius + fontSize / 3}" text-anchor="middle"
              font-size="${fontSize}" fill="${textColor}" font-family="'Roboto Condensed', sans-serif" font-weight="${fontWeight}">
            ${total}
        </text>
    `

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">
            ${greenArc}
            ${redArc}
            ${centerCircle}
            ${text}
        </svg>
    `

    return window.btoa(svg)
}

type ClusterIconOptions = {
    size?: number
    borderWidth?: number
    centerColor?: string
    textColor?: string
    fontSize?: number
    fontFamily?: string
    fontWeight?: string | number
    borderRadius?: number
}

type ClusterIconResult = {
    base64: string
    width: number
    height: number
}

export const generateRoundedSquareClusterIcon = (
    redCount: number,
    greenCount: number,
    options: ClusterIconOptions = {},
): ClusterIconResult => {
    const size = options.size ?? 100
    const borderWidth = options.borderWidth ?? 12
    const centerColor = options.centerColor ?? 'rgba(255, 255, 255, 1)'
    const textColor = options.textColor ?? 'black'
    const fontSize = options.fontSize ?? 40
    const fontFamily = options.fontFamily ?? "'Roboto Condensed', sans-serif"
    const fontWeight = options.fontWeight ?? 550
    const radius = options.borderRadius ?? 20

    const total = redCount + greenCount
    if (total <= 0) {
        return { base64: '', width: size, height: size }
    }

    // Define viewBox and radii
    const innerOffset = borderWidth / 2

    // Use stroke-dasharray on a single outer path
    const redRatio = redCount / total
    const greenRatio = greenCount / total

    // Estimated rounded rectangle path length
    const cornerArc = (Math.PI * radius) / 2
    const edgeLength = size - 2 * radius
    const totalLength = (edgeLength + cornerArc) * 4

    const greenLength = totalLength * greenRatio
    const redLength = totalLength * redRatio

    // Build full path string
    const roundedRectPath = `
        M ${innerOffset + radius},${innerOffset}
        H ${size - innerOffset - radius}
        A ${radius},${radius} 0 0 1 ${size - innerOffset},${innerOffset + radius}
        V ${size - innerOffset - radius}
        A ${radius},${radius} 0 0 1 ${size - innerOffset - radius},${size - innerOffset}
        H ${innerOffset + radius}
        A ${radius},${radius} 0 0 1 ${innerOffset},${size - innerOffset - radius}
        V ${innerOffset + radius}
        A ${radius},${radius} 0 0 1 ${innerOffset + radius},${innerOffset}
        Z
    `

    const greenStroke = `
        <path d="${roundedRectPath.trim()}"
              fill="none"
              stroke="green"
              stroke-width="${borderWidth}"
              stroke-dasharray="${greenLength},${totalLength}"
              stroke-linecap="butt" />
    `

    const redStroke = `
        <path d="${roundedRectPath.trim()}"
              fill="none"
              stroke="red"
              stroke-width="${borderWidth}"
              stroke-dasharray="${redLength},${totalLength}"
              stroke-dashoffset="${-greenLength}"
              stroke-linecap="butt" />
    `

    // Inner box
    const innerRect = `<rect x="${borderWidth}" y="${borderWidth}" width="${size - 2 * borderWidth}" height="${size - 2 * borderWidth}" fill="${centerColor}" rx="${radius - borderWidth / 2}" ry="${radius - borderWidth / 2}" />`

    const text = `<text x="${size / 2}" y="${size / 2 + fontSize / 3}" text-anchor="middle" font-size="${fontSize}" font-family="${fontFamily}" font-weight="${fontWeight}" fill="${textColor}">${total}</text>`

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            ${greenStroke}
            ${redStroke}
            ${innerRect}
            ${text}
        </svg>
    `

    return {
        base64: window.btoa(svg.replace(/[\n\r]+/g, '').trim()),
        width: size,
        height: size,
    }
}

export const generateVerticalSplitClusterIcon = (
    redCount: number,
    greenCount: number,
    options: ClusterIconOptions = {},
): ClusterIconResult => {
    const size = options.size ?? 100
    const fontSize = options.fontSize ?? 40
    const fontFamily = options.fontFamily ?? "'Roboto Condensed', sans-serif"
    const fontWeight = options.fontWeight ?? 550
    const textColor = options.textColor ?? 'white'
    const radius = options.borderRadius ?? 20

    const total = redCount + greenCount
    if (total <= 0) {
        return { base64: '', width: size, height: size }
    }

    const greenRatio = greenCount / total
    const redRatio = redCount / total

    const greenHeight = greenRatio * size
    const redHeight = redRatio * size

    const clipId = 'rounded-box'

    const clipPath = `
        <clipPath id="${clipId}">
            <rect x="0" y="0" width="${size}" height="${size}" rx="${radius}" ry="${radius}" />
        </clipPath>
    `

    const greenRect = `
        <rect x="0" y="0" width="${size}" height="${greenHeight}" fill="green" clip-path="url(#${clipId})" />
    `

    const redRect = `
        <rect x="0" y="${greenHeight}" width="${size}" height="${redHeight}" fill="red" clip-path="url(#${clipId})" />
    `

    const text = `
        <text x="${size / 2}" y="${size / 2 + fontSize / 3}"
              text-anchor="middle"
              font-size="${fontSize}"
              font-family="${fontFamily}"
              font-weight="${fontWeight}"
              fill="${textColor}">
            ${total}
        </text>
    `

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <defs>${clipPath}</defs>
            ${greenRect}
            ${redRect}
            ${text}
        </svg>
    `

    return {
        base64: window.btoa(svg.replace(/[\n\r]+/g, '').trim()),
        width: size,
        height: size,
    }
}

/**
 *
 * @param geoJSON
 * @returns This determines the map type based on the properties field in geoJSON.
 * But this is not valid for all geoJSON. This is UBQ UI only. Where we also store
 * Circle/Polygon type in a custom property called mapType.
 * In general according to RFC geoJSON.features[0].geometry.type is the correct field
 */
const getMapType = (geoJSON: GeoJSON.FeatureCollection) => {
    if (
        geoJSON.features &&
        geoJSON.features.length > 0 &&
        geoJSON.features[0].geometry
    ) {
        return geoJSON.features[0].properties?.mapType
    }
}

export const isPolygonMap = (geoJSON: GeoJSON.FeatureCollection): boolean => {
    const mapType = getMapType(geoJSON)
    return mapType === MapTypes.polygon
}

export const isCircleMap = (geoJSON: GeoJSON.FeatureCollection): boolean => {
    const mapType = getMapType(geoJSON)
    return mapType === MapTypes.circle
}

export const normalizeAddressSearchInput = (address: string): string => {
    return address
        .replaceAll(/[#.,\-_/\\]/g, ' ') // remove special chars
        .replaceAll(/\s+/g, ' ') // collapse multiple spaces
        .replaceAll(/\b(near|opposite|beside|next to|landmark)\b/gi, '') // remove landmarks
        .trim()
}

export const isValidLatitude = (lat: number): boolean => {
    return typeof lat === 'number' && !isNaN(lat) && lat >= -90 && lat <= 90
}

export const isValidLongitude = (lng: number): boolean => {
    return typeof lng === 'number' && !isNaN(lng) && lng >= -180 && lng <= 180
}
